import React, { useEffect, useRef, useState } from "react";
import { EndCustomer } from "@api/graphql/generated/generated-types";
import { Combobox } from "@src/common/components";
import { useDebounce } from "usehooks-ts";
import { UserIcon } from "@heroicons/react/24/solid";
import { GlobeAltIcon } from "@heroicons/react/24/outline";
import classNames from "classnames";
import {
  useCallerContactSearch,
  CallerContactOption,
} from "./useCallerContactSearch";

interface CallerContactInputProps {
  value: string;
  selectedCustomer: EndCustomer | undefined;
  required?: boolean;
  error?: boolean;
  onSelect: (contact: CallerContactOption) => void;
  onChange: (value: string) => void;
}

function CallerContactInput({
  value,
  selectedCustomer,
  required = false,
  error = false,
  onSelect,
  onChange,
}: CallerContactInputProps) {
  const inputRef = useRef<HTMLElement>(null);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce<string>(query, 500);

  const { search, options, loading } = useCallerContactSearch(
    selectedCustomer?.id
  );

  useEffect(() => {
    if (debouncedQuery.length > 0) {
      search(debouncedQuery);
    }
  }, [debouncedQuery]);

  const handleChange = (
    nextValue: string,
    selectedOption?: CallerContactOption | null
  ) => {
    setQuery(nextValue);
    if (selectedOption) {
      onSelect(selectedOption);
    } else {
      onChange(nextValue);
    }
  };

  const CallerContactListItem: React.FC<{
    isSelected: boolean;
    option: unknown;
  }> = ({ option }) => {
    const contact = option as CallerContactOption;

    return (
      <div
        className={classNames({
          "flex justify-between w-full hover:cursor-pointer": true,
          "opacity-50": !contact.isSelectedCustomer,
        })}
      >
        <div className="w-4/5 flex flex-col">
          <div className="text-xs font-bold truncate dark:text-gray-100">
            {contact.name}
          </div>
          <div className="text-xs dark:text-gray-300">
            {contact.phone}
            {!contact.isSelectedCustomer &&
              contact.endCustomerName &&
              `${contact.phone ? " · " : ""}${contact.endCustomerName}`}
          </div>
        </div>
        <div className="w-1/5 flex justify-end">
          {contact.isSelectedCustomer ? (
            <UserIcon className="w-5 h-5 text-green-500" />
          ) : (
            <GlobeAltIcon className="w-5 h-5 text-blue-500" />
          )}
        </div>
      </div>
    );
  };

  return (
    <Combobox
      size="mini"
      inputRef={inputRef}
      error={error}
      onChange={(nextValue, selectedOption) =>
        handleChange(nextValue, selectedOption as CallerContactOption | null)
      }
      mapOptionToString={(option) =>
        (option as CallerContactOption)?.name || ""
      }
      value={value}
      name="caller-contact-options"
      options={options}
      mapOptionToNode={CallerContactListItem}
      overrides={{
        Input: {
          props: {
            placeholder: selectedCustomer
              ? "Search for a caller contact"
              : "",
            error,
            autoComplete: "off",
          },
        },
        ListItem: {
          style: {
            paddingTop: "5px",
            paddingBottom: "5px",
            height: "auto",
          },
        },
      }}
    />
  );
}

export default CallerContactInput;
export type { CallerContactInputProps, CallerContactOption };
